import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  afterNextRender,
} from '@angular/core';

import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType } from '@app/shared/models/modal.model';
import { SharedModule } from '@app/shared/shared.module';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DragScrollDirective } from '@app/shared/directives/drag-and-scroll.directive';
import { GsapService } from '@app/shared/animations/gsap.service';
import { RING_BRONCE_MAX_LEVEL, RING_GOLD_MIN_LEVEL, RingTypeEnum } from '@app/progress-line/progress-line.model';

type CasinoState = 'current' | 'locked' | 'completed';

interface CasinoView {
  order: number;
  tier: RingTypeEnum;
  state: CasinoState;
  even: boolean;
}

@Component({
  selector: 'slotcom-casino-map-modal',
  templateUrl: './casino-map.component.html',
  styleUrls: ['./casino-map.component.scss'],
  standalone: true,
  imports: [SharedModule, DragScrollDirective],
})
export class CasinoMapComponent implements AfterViewInit {
  @ViewChild('casinoMapContainer', { read: ElementRef })
  casinoMapContainerRef!: ElementRef<HTMLElement>;

  @ViewChild('scrollContainer', { read: ElementRef })
  scrollContainerRef!: ElementRef<HTMLElement>;

  @ViewChildren('cloudBack', { read: ElementRef })
  cloudBackRefs!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('cloudFront', { read: ElementRef })
  cloudFrontRefs!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('casinoSpark', { read: ElementRef })
  casinoSparksRef!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('casinoPointer', { read: ElementRef })
  casinoPointerRefs!: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('casinoLogo', { read: ElementRef })
  casinoLogoRefs!: QueryList<ElementRef<HTMLElement>>;

  private readonly gsapService = inject(GsapService);
  private readonly eventManager = inject(EventManager);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly casinoMapModalData = signal(inject<Modal>(MAT_DIALOG_DATA));

  readonly casinoList: CasinoView[] = this.buildCasinoList();
  readonly roads = Array(this.casinoList.length + 1);
  readonly clouds = Array(Math.ceil(this.casinoList.length / 4));

  constructor() {
    this.host.nativeElement.style.setProperty('--casino-amount', String(this.casinoList.length));
    afterNextRender(() => this.centerOnCurrentCasino());
  }

  ngAfterViewInit(): void {
    this.cloudBackRefs.forEach((ref) => {
      this.gsapService.sway(ref.nativeElement, { y: 1, duration: 2, reverse: false });
    });
    this.cloudFrontRefs.forEach((ref) => {
      this.gsapService.sway(ref.nativeElement, { y: 1, duration: 2, reverse: true });
    });
    this.casinoSparksRef.forEach((ref) => {
      this.gsapService.scaleInOut(ref.nativeElement, { duration: 0.35, holdIn: 0.6, holdOut: 0.15 });
    });

    const currentPointerRef = this.casinoPointerRefs.get(this.casinoMapModalData().data.order - 1);
    if (currentPointerRef) {
      this.gsapService.slideIn(currentPointerRef.nativeElement, 'top', 80, {
        delay: 0.5,
        duration: 1.4,
        bounce: true,
      });
    }
  }

  showLockedInfo(glyph: HTMLElement, info: HTMLElement): void {
    this.gsapService.lockedInfoSequence(glyph, info);
  }

  centerOnCurrentCasino(): void {
    const vp = this.scrollContainerRef?.nativeElement;
    if (!vp) return;

    const currentIndex = this.casinoMapModalData().data.order - 1;
    const currentLogoRef = this.casinoLogoRefs.get(currentIndex);
    if (!currentLogoRef) return;

    const logoEl = currentLogoRef.nativeElement;
    const logoOffsetLeft = logoEl.offsetLeft + (logoEl.offsetParent as HTMLElement)?.offsetLeft;
    const targetLeft = logoOffsetLeft + logoEl.offsetWidth / 2 - vp.clientWidth / 2;
    const clampedLeft = Math.max(0, Math.min(targetLeft, vp.scrollWidth - vp.clientWidth));

    vp.scrollLeft = clampedLeft;
  }

  onCurrentCasinoClick(logoEl: HTMLElement): void {
    this.gsapService.pressDown(logoEl, {
      duration: 0.1,
    });
    this.gsapService.fadeOut(this.casinoMapContainerRef.nativeElement, {
      duration: 0.1,
      onComplete: () => this.closeCasinoMap(),
    });
  }

  closeCasinoMap(): void {
    this.eventManager.closeModal(new Modal(this.casinoMapModalData().id, ModalType.View));
  }

  private buildCasinoList(): CasinoView[] {
    const total = this.casinoMapModalData().data?.totalCasinos ?? 0;
    const current = this.casinoMapModalData().data?.order ?? 0;

    return Array.from({ length: total }, (_, i) => {
      const order = i + 1;
      const state: CasinoState = order === current ? 'current' : order > current ? 'locked' : 'completed';
      return {
        order,
        tier: this.getTierByOrder(order),
        state,
        even: i % 2 === 0,
      };
    });
  }

  private getTierByOrder(order: number): RingTypeEnum {
    if (order >= RING_GOLD_MIN_LEVEL) return RingTypeEnum.GOLD;
    if (order > RING_BRONCE_MAX_LEVEL) return RingTypeEnum.SILVER;
    return RingTypeEnum.BRONCE;
  }
}
